/***************************************************************************/
"use client";
/***************************************************************************/

import { TPermissions } from "@/app/api/user/me/permissions/interfaces.universal";
import { TPermission } from "@/universal/types/rbac";
import React, { createContext, useContext } from "react";
import { useQuery } from "../api/use-query";

const RBACContext = createContext<TPermission[]>([]);

const RBACProvider: React.FC<{
  children: React.ReactNode;
  permissions: TPermission[];
}> = ({ children, permissions }) => {
  const { data: fetchedPermissions } = useQuery<TPermissions>(
    "/user/me/permissions",
  );

  return (
    <RBACContext.Provider value={fetchedPermissions ?? permissions ?? []}>
      {children}
    </RBACContext.Provider>
  );
};

const useIsAuthorized = () => {
  const userPermissions = useContext(RBACContext);

  return (permissions: TPermission[]) => {
    const isAuthorized = !!permissions.find((permission) => {
      return !!userPermissions.find((userPermission) => {
        return userPermission === permission;
      });
    });
    return isAuthorized;
  };
};

const RBAC: React.FC<{
  children: React.ReactNode;
  permissions: TPermission[];
}> = ({ permissions, children }) => {
  const isAuthorized = useIsAuthorized();

  if (isAuthorized(permissions)) return children;

  return null;
};

const withRBAC = <TProps,>(
  Component: React.ComponentType<TProps>,
  permissions: TPermission[],
) => {
  const WithRBAC: React.FC<TProps & React.JSX.IntrinsicAttributes> = (
    props,
  ) => {
    return (
      <RBAC permissions={permissions}>
        <Component {...props} />
      </RBAC>
    );
  };

  return WithRBAC;
};

export { RBAC, RBACProvider, useIsAuthorized, withRBAC };
